(ns hai.home.mobile.mini-apps.progress
  "My Progress mini-app card for the mobile dashboard: today's meetings and
   completed-task stamps on a vertical day strip with a red 'now' line. The
   window/axis/stamp math is shared with the desktop card via [[hai.home.progress]]
   (the desktop card lays it out horizontally); meeting state comes from
   [[hai.home.calendar]], completions from [[hai.tasks.data]], and the row of
   deadlines and all-day entries above the strip from [[hai.home.day-pills]]."
  (:require
   [hai.datetime :as datetime]
   [hai.home.calendar :as calendar]
   [hai.home.day-pills :as day-pills]
   [hai.home.mobile.mini-apps :as mini-apps]
   [hai.home.progress :as progress]
   [hai.tasks.data :as tasks-data]
   [hai.tasks.ui :as ui]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; Same glyph as the navbar's "Progress timeline" route (see hai.journal2.routes).
(def ^:private timeline-icon (icons/mui-icon :outlined :timeline))

(def ^:private strip-height-px 360)

(defn- pct [num total]
  (* 100 (/ num total)))

;; Inset all cross-axis lanes by ~2% each side so blocks don't touch the strip
;; edges (replaces the old fixed 6px left/right gutter).
(def ^:private cross-gutter 0.02)

(defn- positioned-block
  "Per-meeting position within the window, or nil if it doesn't intersect.
   Main axis (time) comes from [[progress/meeting-span-pct]]; cross axis (lane)
   → {:left-pct :width-pct} from the shared layout record."
  [meeting layout win]
  (when-let [{:keys [offset-pct extent-pct]} (progress/meeting-span-pct meeting win)]
    (let [inset (- 1 (* 2 cross-gutter))]
      {:top-pct    offset-pct
       :height-pct extent-pct
       :left-pct   (* 100 (+ cross-gutter (* (:cross-start layout 0) inset)))
       :width-pct  (* 100 (:cross-size layout 1) inset)})))

;; ---------------------------------------------------------------------------
;; Card frame (soft sky-blue header from the shared card palette)
;; ---------------------------------------------------------------------------

(o/defstyled card mini-apps/card
  [:.app-header
   {:background t/--progress
    :border-bottom "1px solid" :border-bottom-color t/--card-forest-10}]
  ;; :timeline is a Material fill icon, unlike the stroke icons the shared card
  ;; frame assumes, so restore fill and match the other header icons' size.
  [:.app-icon [:svg {:fill "currentColor" :width "20px" :height "20px"}]]
  [:.progress-body
   {:padding "14px 16px 18px"
    :display "flex" :flex-direction "column" :gap "12px"}]
  [:.progress-subtitle
   {:font-size "12px" :font-weight "500"
    :color t/--property-15
    :margin "0"
    :display "flex" :flex-wrap "wrap" :align-items "center"
    :column-gap "6px" :row-gap "4px"}
   [:strong {:color t/--card-forest :font-weight "700"}]])

(o/defstyled timeline :div
  {:display "grid"
   :gap "8px"})

(o/defstyled time-gutter :div
  {:position "relative"
   :font-variant-numeric "tabular-nums"}
  [:.tick
   {:position "absolute"
    :right "0"
    :transform "translateY(-50%)"
    :font-size "11px" :font-weight "500"
    :color t/--link-muted
    :line-height "1"}])

(o/defstyled strip :div
  {:position "relative"
   :border-radius "10px"
   :background "repeating-linear-gradient(-45deg, rgba(184,196,166,0.10), rgba(184,196,166,0.10) 4px, rgba(184,196,166,0.02) 4px, rgba(184,196,166,0.02) 8px)"}
  [:.block
   ;; left + width come from inline style (cross-axis lane); see positioned-block.
   {:position "absolute"
    :border-radius "6px"
    :padding "3px 8px"
    :background "#A8BA93"
    :color t/--card-forest
    :font-size "11px" :font-weight "700"
    :line-height "1.2"
    :overflow "hidden"
    :text-overflow "ellipsis"
    :white-space "nowrap"
    :box-shadow "0 1px 2px rgba(45,62,42,0.14)"}]
  [:.block.past
   {:background "#C5CCB8" :color "rgba(45,62,42,0.55)"
    :box-shadow "none"}]
  [:.block.now
   {:box-shadow "0 0 0 2px #C44545, 0 1px 2px rgba(45,62,42,0.18)"}])

;; Completion stamps ride a narrow lane to the right of the strip, each cluster
;; pinned to its mean completion time. A burst piles up (opaque stamps occlude
;; the ones behind) so several done in a window read as a small stack. Tapping a
;; cluster fans the pile apart and reveals a popover of its tasks — the touch
;; stand-in for the desktop card's hover fan + tooltip.
(o/defstyled stamp-lane :div
  {:position "relative"}
  [:.stamp-cluster
   {:position "absolute"
    :left "0" :right "0"
    :transform "translateY(-50%)"
    :display "flex" :flex-direction "column" :align-items "center"
    :cursor "pointer"
    :z-index "6"}
   [:.progress-stamp
    {:width "26px" :height "26px" :flex "0 0 auto"
     :transition "margin 160ms ease"}
    [ui/completion-stamp
     {:width "100%" :height "100%"
      :background-color t/--card-surface
      :border-color t/--card-forest
      :color t/--card-forest
      :mix-blend-mode "normal"
      :opacity "1"}]
    [:.check {:font-size "17px"}]]
   [:.progress-stamp+.progress-stamp {:margin-top "-16px"}]
   ;; Tapped open: raise above neighbours and space the pile out.
   [:&.open
    {:z-index "8"}
    [:.progress-stamp+.progress-stamp {:margin-top "4px"}]]]
  ;; Task details, floated left over the strip so the narrow lane doesn't clip it.
  ;; `right: 100%` + margin (not calc — garden strips calc's inner spaces) opens
  ;; it toward the card interior.
  [:.stamp-popover
   {:position "absolute"
    :right "100%"
    :margin-right "8px"
    :top "50%"
    :transform "translateY(-50%)"
    :min-width "150px" :max-width "220px"
    :background t/--card-surface
    :border-radius "10px"
    :box-shadow "0 2px 4px rgba(45,62,42,0.06), 0 8px 22px rgba(45,62,42,0.18)"
    :padding "8px 10px"
    :display "flex" :flex-direction "column" :gap "6px"
    :z-index "9"}
   [:.stamp-pop-row
    {:display "flex" :flex-direction "column" :gap "1px"}]
   [:.stamp-pop-title
    {:font-size "12px" :font-weight "600" :color t/--card-forest
     :line-height "1.3" :word-break "break-word"}]
   [:.stamp-pop-time
    {:font-size "11px" :color t/--link-muted
     :font-variant-numeric "tabular-nums"}]])

(o/defstyled now-line :div
  {:position "absolute"
   :left "0" :right "0"
   :height "0"
   :border-top "2px solid #C44545"
   :z-index "5"
   :pointer-events "none"}
  [:.now-label
   {:position "absolute"
    :right "100%"
    :margin-right "8px"
    :top "-9px"
    :background "#C44545" :color "white"
    :font-size "10px" :font-weight "700"
    :padding "2px 7px"
    :border-radius "999px"
    :white-space "nowrap"
    :font-variant-numeric "tabular-nums"
    :box-shadow "0 1px 2px rgba(45,62,42,0.20)"}])

;; ---------------------------------------------------------------------------
;; Views
;; ---------------------------------------------------------------------------

(defn- render-block [{:keys [meeting status top-pct height-pct left-pct width-pct]}]
  (let [title       (calendar/m-title meeting)
        height-px   (* (/ height-pct 100) strip-height-px)
        show-title? (>= height-px 22)]
    [:div.block
     {:class (name (or status :upcoming))
      :style {:top    (str top-pct "%")
              :height (str height-pct "%")
              :left   (str left-pct "%")
              :width  (str width-pct "%")}
      :title title}
     (when show-title? title)]))

(defn- stamp-cluster-view
  "One completion burst pinned to its mean time. `open?` (toggled by `on-toggle`
   on tap) fans the pile and shows the task list — the touch replacement for the
   desktop hover fan + tooltip."
  [{:keys [group win open? on-toggle]}]
  (let [{:keys [tasks mean-ms]} group
        pct-val (progress/stamp-pct mean-ms win)]
    [:div.stamp-cluster
     {:class (when open? "open")
      :style {:top (str pct-val "%")}
      :on-click (fn [e] (.stopPropagation e) (on-toggle))}
     (doall
      (for [task tasks]
        ^{:key (:task/uuid task)}
        [:div.progress-stamp
         [ui/completion-stamp {:task-title (:task/title task)}]]))
     (when open?
       ;; Keep taps on the details themselves from bubbling to the cluster
       ;; toggle (or the timeline's dismiss) and closing the popover.
       (into [:div.stamp-popover {:on-click #(.stopPropagation %)}]
             (map (fn [task]
                    ^{:key (:task/uuid task)}
                    [:div.stamp-pop-row
                     [:span.stamp-pop-title (:task/title task)]
                     [:span.stamp-pop-time (datetime/format-time (:task/completed-at task))]]))
             tasks))]))

(defn panel []
  (r/with-let [!open-stamp (r/atom nil)]
    (let [meetings    @(<s [::calendar/timed-meetings])
          all-day     @(<s [::calendar/all-day-meetings])
          deadlines   @(<s [::tasks-data/deadlines-today])
          completions @(<s [::tasks-data/completed-today])
          layout      @(<s [::calendar/meeting-layout])
          active?     @(<s [::calendar/any-cal-active?])
          loading?    (or @(<s [::calendar/loading?])
                          (not @(<s [::calendar/connection-status-initialized?])))
          error?      @(<s [::calendar/error?])
          open-stamp  @!open-stamp
          win          (progress/compute-window meetings completions)
          hours        (progress/axis-hours win)
          now%         (progress/now-pct win)
          stamp-groups (progress/cluster-completions completions)
          done-count    (count completions)
          meeting-count (count meetings)]
      [card
       [:div.app-header
        [:a.app-title-link
         {:href (routing/href :timelines/view)
          :on-click (fn [e]
                      (.preventDefault e)
                      (>e [:routing/navigate [:timelines/view]]))}
         [:span.app-icon [timeline-icon]]
         [:h2.app-title "My Progress"]]]
       (cond
         loading? nil

         error?
         [:div.app-placeholder "Failed to load calendar events."]

         ;; Completed-task stamps plot without a calendar, so the connect
         ;; prompt only covers a day with nothing to draw at all.
         (and (not active?) (empty? completions) (empty? deadlines))
         [:div.app-placeholder "Connect your calendar to see today's progress."]

         (and (empty? meetings) (empty? all-day) (empty? completions)
              (empty? deadlines))
         [:div.app-placeholder "No meetings or completed tasks yet today."]

         :else
         [:div.progress-body
          (when (or (pos? done-count) (seq all-day) (seq deadlines))
            [:div.progress-subtitle
             ;; The counts live in one span so the flex row (needed for the
             ;; pills) doesn't swallow the spaces between the text nodes.
             (when (pos? done-count)
               [:span
                (when (pos? meeting-count)
                  [:<>
                   [:strong (str meeting-count)]
                   (if (= 1 meeting-count) " meeting · " " meetings · ")])
                [:strong (str done-count)]
                (if (= 1 done-count) " task done" " tasks done")
                " — steady pace"])
             (when (or (seq deadlines) (seq all-day))
               [day-pills/day-pills {:deadlines deadlines
                                     :all-day   all-day
                                     :compact?  true}])])
          [timeline
           {:style {:grid-template-columns (if (seq stamp-groups) "38px 1fr 34px" "38px 1fr")}
            ;; Tapping anywhere but a stamp cluster dismisses its open details.
            :on-click #(reset! !open-stamp nil)}
           [time-gutter {:style {:height (str strip-height-px "px")}}
            (doall
              (for [h hours]
                ^{:key h}
                [:span.tick
                {:style {:top (str (pct (- h (:start-hour win))
                                        (/ (:total-ms win) (* 60 60 1000)))
                                   "%")}}
                (datetime/format-hour-recognizable h)]))]
           [strip {:style {:height (str strip-height-px "px")}}
            (doall
             (for [m meetings
                   :let [lout (get layout (:journal.entry/uuid m))
                         pos  (positioned-block m lout win)]
                   :when pos]
               ^{:key (:journal.entry/uuid m)}
               [render-block (assoc pos
                                    :meeting m
                                    :status (::calendar/status m))]))
            (when now%
              [now-line {:style {:top (str now% "%")}}
               [:span.now-label (datetime/format-time (js/Date.))]])]
           (when (seq stamp-groups)
             [stamp-lane {:style {:height (str strip-height-px "px")}}
              (doall
               (for [group stamp-groups]
                 (let [gid (:task/uuid (first (:tasks group)))]
                   ^{:key gid}
                   [stamp-cluster-view
                    {:group     group
                     :win       win
                     :open?     (= open-stamp gid)
                     :on-toggle #(swap! !open-stamp (fn [cur] (if (= cur gid) nil gid)))}])))])]])])))
